import { useCallback, useRef } from "react";
import usePush from "../hooks/usePush";
import useOnUnmount from "../hooks/useOnUnmount";
import AccountService from "../services/account-service";
import ManageService from "../services/manage-service";
import config from "../config";

let chargebeeLoadPromise = null;
const loadAndInitializeChargebee = async () => {
  if (!chargebeeLoadPromise) {
    chargebeeLoadPromise = Promise.resolve()
      .then(
        () =>
          new Promise((resolve) => {
            const script = document.createElement("script");
            script.src = "https://js.chargebee.com/v2/chargebee.js";
            script.onload = () => {
              if (!window.Chargebee) return;

              resolve(
                window.Chargebee.init({
                  site: config.chargebeeSite,
                }),
              );
            };

            document.head.appendChild(script);
          }),
      )
      .catch((err) => {
        throw err;
      });
  }

  return chargebeeLoadPromise;
};

// eslint-disable-next-line import/prefer-default-export
export const useChargebeePortal = ({ openSubscription, shortCode } = {}) => {
  const { execute } = usePush(AccountService.createChargebeePortalSession, {
    errorToast: true,
  });

  const instance = useRef();

  const clickCallback = useCallback(async () => {
    instance.current = await loadAndInitializeChargebee();

    instance.current.setPortalSession(async () => {
      const [err, val] = await execute({ shortCode });

      if (err) throw err;
      return val;
    });

    const chargebeeInstance = instance.current;
    const cbPortal = chargebeeInstance.createChargebeePortal();

    const portalOptions = {
      close: () => chargebeeInstance.logout(),
    };

    if (openSubscription) {
      cbPortal.open(portalOptions, {
        sectionType: window.Chargebee.getPortalSections().SUBSCRIPTION_DETAILS,
        params: { subscriptionId: openSubscription },
      });
    } else {
      cbPortal.open(portalOptions);
    }
  }, [execute, openSubscription, shortCode]);

  useOnUnmount(() => {
    if (instance.current) {
      instance.current.logout();
    }
  });

  return {
    clickCallback,
  };
};

export const useChargebeeCheckout = ({
  planName,
  currency,
  period,
  moduleName,
  organisationShortCode,
  chargebeeOptions = {},
}) => {
  const { execute } = usePush(ManageService.createCheckoutHostedPage);

  const clickCallback = useCallback(async () => {
    const chargebeeInstance = await loadAndInitializeChargebee();

    await chargebeeInstance.openCheckout({
      ...chargebeeOptions,
      async hostedPage() {
        const [err, val] = await execute(organisationShortCode, {
          planName,
          currency,
          period,
          moduleName,
        });

        if (err) throw err;
        return val;
      },
    });
  }, [
    chargebeeOptions,
    currency,
    execute,
    moduleName,
    organisationShortCode,
    period,
    planName,
  ]);

  return { clickCallback };
};
